import React, { useState } from 'react';
import { DollarSign, Calendar, CheckCircle, XCircle, Clock, MessageSquare, Heart } from 'lucide-react';
import CountdownTimer from './CountdownTimer';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import VerificationRequiredModal from './verification/VerificationRequiredModal';
import { isSecondaryVerified, isSecondaryVerificationError } from '../utils/verificationGate';

/**
 * CustomRequestCard Component
 * Displays custom request in chat messages
 * Status: pending, accepted, countered, rejected, expired, cancelled
 */
const CustomRequestCard = ({ request, currentUserId, onUpdate, onCounter }) => {
    const { user, isAuthenticated } = useAuth();

    // Initial State Hydration based on global auth state
    const [isSaved, setIsSaved] = useState(
        () => user?.savedRequests?.includes(request.id || request._id) || false
    );
    const [isSaving, setIsSaving] = useState(false);

    const [loading, setLoading] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [rejectNote, setRejectNote] = useState('');
    const [showRejectInput, setShowRejectInput] = useState(false);

    const toggleSave = async (e) => {
        e.preventDefault();

        if (!isAuthenticated) {
            toast.error("Please login to save requests");
            return;
        }

        // Optimistic UI update
        setIsSaved(prev => !prev);
        setIsSaving(true);

        try {
            await api.post(`/users/favorites/requests/${request.id || request._id}`);
        } catch (error) {
            console.error("Failed to toggle save:", error);
            setIsSaved(prev => !prev);
            toast.error(error.response?.data?.message || "Failed to update favorites");
        } finally {
            setIsSaving(false);
        }
    };

    const isRecipient = (request.requestedTo.id || request.requestedTo._id) === currentUserId; // Gigger
    const isSender = (request.requestedBy.id || request.requestedBy._id) === currentUserId; // Shipper
    const isPending = request.status === 'pending';
    const isExpired = request.status === 'expired' || new Date(request.expiresAt) < new Date();

    const handleAccept = async () => {
        // Accepting creates the order and holds the escrow, so it needs Secondary; the server enforces it too
        if (user && !isSecondaryVerified(user)) {
            setShowVerificationModal(true);
            return;
        }
        try {
            setLoading(true);
            const response = await api.patch(`/requests/${request.id || request._id}/accept`);

            toast.success('Request accepted! Order created successfully.');

            if (onUpdate) {
                onUpdate(response.data.data);
            }

            // Navigate to order page if order was created
            if (response.data.data.order) {
                // window.location.href = `/orders/${response.data.data.order.id || response.data.data.order._id}`;
            }
        } catch (error) {
            if (isSecondaryVerificationError(error)) {
                setShowVerificationModal(true);
            } else {
                toast.error(error.response?.data?.message || 'Failed to accept request');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        if (!rejectNote.trim()) {
            toast.error('Please provide a reason for rejecting');
            return;
        }

        try {
            setLoading(true);
            await api.patch(`/requests/${request.id || request._id}/reject`, { note: rejectNote });

            toast.success('Request rejected');
            setShowRejectInput(false);

            if (onUpdate) {
                onUpdate({ ...request, status: 'rejected', response: { note: rejectNote } });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to reject request');
        } finally {
            setLoading(false);
        }
    };

    const handleCounter = () => {
        // Open CustomOfferModal with pre-filled data from request
        if (onCounter) {
            onCounter(request);
        }
    };

    const getStatusBadge = () => {
        const badges = {
            pending: { color: 'bg-blue-100 text-blue-600', text: 'Pending' },
            accepted: { color: 'bg-green-100 text-green-600', text: 'Accepted' },
            countered: { color: 'bg-orange-100 text-orange-600', text: 'Countered' },
            rejected: { color: 'bg-gray-100 text-gray-600', text: 'Rejected' },
            expired: { color: 'bg-red-100 text-red-600', text: 'Expired' },
            cancelled: { color: 'bg-gray-100 text-gray-600', text: 'Cancelled' }
        };

        const status = isExpired && isPending ? 'expired' : request.status;
        const badge = badges[status] || badges.pending;

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                {badge.text}
            </span>
        );
    };

    const getBorderColor = () => {
        if (isExpired || request.status === 'expired') return 'border-red-300';
        if (request.status === 'accepted') return 'border-green-300';
        if (request.status === 'countered') return 'border-orange-300';
        if (request.status === 'rejected' || request.status === 'cancelled') return 'border-gray-300';
        return 'border-blue-300';
    };

    return (
        <div className={`border-2 ${getBorderColor()} rounded-lg p-4 bg-white shadow-sm max-w-md`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900">Custom Request</h4>
                        <p className="text-xs text-gray-500">from @{request.requestedBy.username}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={toggleSave}
                        disabled={isSaving}
                        className="p-1.5 rounded-full hover:bg-gray-100 transition-colors duration-200 group"
                        aria-label={isSaved ? "Remove from favorites" : "Save to favorites"}
                    >
                        <Heart
                            className={`w-5 h-5 transition-colors duration-200 ${isSaved
                                    ? 'fill-red-500 text-red-500'
                                    : 'text-gray-400 group-hover:text-red-500'
                                }`}
                        />
                    </button>
                    {getStatusBadge()}
                </div>
            </div>

            {/* Content */}
            <div className="space-y-2 mb-3">
                <h3 className="font-semibold text-lg text-gray-900">{request.request.title}</h3>
                <p className="text-sm text-gray-600 line-clamp-3">{request.request.description}</p>
            </div>

            {/* Details */}
            <div className="grid grid-cols-2 gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4 text-gray-600" />
                    <div>
                        <p className="text-xs text-gray-500">Budget</p>
                        <p className="font-semibold text-sm">
                            {request.request.budget.displayCurrency} {request.request.budget.displayAmount?.toLocaleString()}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    <div>
                        <p className="text-xs text-gray-500">Needed in</p>
                        <p className="font-semibold text-sm">{request.request.desiredDeliveryDays} days</p>
                    </div>
                </div>
            </div>

            {/* Requirements */}
            {request.request.requirements && request.request.requirements.length > 0 && (
                <div className="mb-3">
                    <p className="text-xs font-semibold text-gray-700 mb-1">Requirements:</p>
                    <ul className="list-disc list-inside text-xs text-gray-600 space-y-0.5">
                        {request.request.requirements.slice(0, 3).map((req, idx) => (
                            <li key={idx}>{req}</li>
                        ))}
                        {request.request.requirements.length > 3 && (
                            <li className="text-blue-600">+{request.request.requirements.length - 3} more</li>
                        )}
                    </ul>
                </div>
            )}

            {/* Countdown / Status Message */}
            {isPending && !isExpired && (
                <div className="mb-3">
                    <CountdownTimer
                        targetDate={request.expiresAt}
                        type="expiration"
                        onExpire={() => {
                            if (onUpdate) {
                                onUpdate({ ...request, status: 'expired' });
                            }
                        }}
                    />
                </div>
            )}

            {request.status === 'accepted' && request.order && (
                <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <p className="text-sm text-green-700">
                            Order {request.order.orderNumber || 'created'} successfully!
                        </p>
                    </div>
                </div>
            )}

            {request.status === 'countered' && request.counterOffer && (
                <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded">
                    <div className="flex items-center space-x-2">
                        <MessageSquare className="w-4 h-4 text-orange-600" />
                        <p className="text-sm text-orange-700">
                            Gigger sent a counter-offer. Check your messages.
                        </p>
                    </div>
                </div>
            )}

            {(request.status === 'rejected' || request.status === 'cancelled') && request.response?.note && (
                <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded">
                    <p className="text-xs text-gray-500 mb-1">Reason:</p>
                    <p className="text-sm text-gray-700">{request.response.note}</p>
                </div>
            )}

            {isExpired && isPending && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded">
                    <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-red-600" />
                        <p className="text-sm text-red-700">This request has expired</p>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            {isRecipient && isPending && !isExpired && (
                <div className="space-y-2">
                    {!showRejectInput ? (
                        <div className="space-y-2">
                            <button
                                onClick={handleAccept}
                                disabled={loading}
                                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm flex items-center justify-center space-x-1"
                            >
                                <CheckCircle className="w-4 h-4" />
                                <span>{loading ? 'Accepting...' : 'Accept Request'}</span>
                            </button>

                            <div className="flex space-x-2">
                                <button
                                    onClick={handleCounter}
                                    disabled={loading}
                                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium text-sm flex items-center justify-center space-x-1"
                                >
                                    <MessageSquare className="w-4 h-4" />
                                    <span>Counter Offer</span>
                                </button>
                                <button
                                    onClick={() => setShowRejectInput(true)}
                                    disabled={loading}
                                    className="flex-1 px-4 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 font-medium text-sm flex items-center justify-center space-x-1"
                                >
                                    <XCircle className="w-4 h-4" />
                                    <span>Reject</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <textarea
                                value={rejectNote}
                                onChange={(e) => setRejectNote(e.target.value)}
                                placeholder="Please provide a reason for rejecting..."
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                rows="2"
                            />
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleReject}
                                    disabled={loading || !rejectNote.trim()}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                                >
                                    {loading ? 'Rejecting...' : 'Confirm Reject'}
                                </button>
                                <button
                                    onClick={() => setShowRejectInput(false)}
                                    disabled={loading}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {showVerificationModal && (
                <VerificationRequiredModal
                    isOpen
                    onClose={() => setShowVerificationModal(false)}
                    returnUrl={window.location.pathname + window.location.search}
                />
            )}
        </div>
    );
};

export default CustomRequestCard;
